import styled from '@emotion/styled'
import {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  Ref,
  useState,
} from 'react'
import { z } from 'zod'

import { Label, LabelPlacement } from './Label'
import { Color } from '../../constants'
import { InputStylesProps, inputStyles } from '../../constants/styles'
import { useValidate } from '../../hooks/useValidate'
import { StyleProps } from '../../utils'
import { ErrorMessage } from '../ErrorMessage'
import { IconName } from '../Icon'

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
 */
export type InputAutoComplete =
  | 'additional-name'
  | 'address-level1'
  | 'address-level2'
  | 'address-level3'
  | 'address-level4'
  | 'address-line1'
  | 'address-line2'
  | 'address-line3'
  | 'bday-day'
  | 'bday-month'
  | 'bday-year'
  | 'bday'
  | 'cc-additional-name'
  | 'cc-csc'
  | 'cc-exp-month'
  | 'cc-exp-year'
  | 'cc-exp'
  | 'cc-family-name'
  | 'cc-given-name'
  | 'cc-name'
  | 'cc-number'
  | 'cc-type'
  | 'country-name'
  | 'country'
  | 'current-password'
  | 'email'
  | 'family-name'
  | 'given-name'
  | 'honorific-prefix'
  | 'honorific-suffix'
  | 'impp'
  | 'language'
  | 'name'
  | 'new-password'
  | 'nickname'
  | 'off'
  | 'on'
  | 'one-time-code'
  | 'organization-title'
  | 'organization'
  | 'photo'
  | 'postal-code'
  | 'sex'
  | 'street-address'
  | 'tel-area-code'
  | 'tel-country-code'
  | 'tel-extension'
  | 'tel-local'
  | 'tel-national'
  | 'tel'
  | 'transaction-amount'
  | 'transaction-currency'
  | 'url'
  | 'username'

export type InputProps = InputStylesProps & {
  autoFocus?: boolean
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters'
  autoComplete?: InputAutoComplete
  /**
   * TODO(rb): rename to `labelDescription` (or remove entirely)
   */
  description?: string
  disabled?: boolean
  /**
   * If specified, this function will be called with the input's value on blur,
   * before it is passed to `onChange`.
   */
  formatter?: (value: string) => string
  id?: string
  innerRef?: Ref<HTMLInputElement>
  label?: string
  labelIcon?: IconName
  labelColor?: Color
  nativeOnChange?: ChangeEventHandler<HTMLInputElement>
  onBlur?: FocusEventHandler<HTMLInputElement>
  onChange?: (value: string) => void
  onClick?: MouseEventHandler<HTMLInputElement>
  onFocus?: FocusEventHandler<HTMLInputElement>
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  placeholder?: string
  required?: boolean
  styles?: StyleProps
  value?: string | null
  schema?: z.ZodTypeAny
  errorMessage?: string
  reserveErrorSpace?: boolean
  forceShowError?: boolean
  placement?: LabelPlacement
}

export const InputElement = styled.input<InputStylesProps>(inputStyles)

export const Input = (props: InputProps) => {
  const [touched, setTouched] = useState(false)

  const value = props.type === 'number' ? Number(props.value) : props.value

  const validateError = useValidate(props.schema, props.required, value)

  const handleBlur: FocusEventHandler<HTMLInputElement> = (event) => {
    if (props.onChange && props.formatter) {
      props.onChange(props.formatter(event.target.value))
    }

    props.onBlur?.(event)
    setTouched(true)
  }

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    props.nativeOnChange?.(event)
    props.onChange?.(event.target.value)
  }

  const errorMessage = props.errorMessage ?? validateError
  const showError = (touched || props.forceShowError) && !!errorMessage

  const inputAndErrorMessage = (
    <>
      <InputElement
        autoFocus={props.autoFocus}
        autoComplete={
          // We'll use "nope" as a nonsense value, as some browsers ignore "off":
          props.autoComplete === 'off' ? 'nope' : props.autoComplete
        }
        autoCapitalize={props.autoCapitalize}
        bold={props.bold}
        disabled={props.disabled}
        fullWidth={props.fullWidth}
        icon={props.icon}
        invalid={showError}
        onBlur={handleBlur}
        onChange={handleChange}
        onClick={props.onClick}
        onFocus={props.onFocus}
        id={props.id}
        onKeyDown={props.onKeyDown}
        placeholder={
          props.placeholder || (props.label ? `${props.label}...` : undefined)
        }
        required={props.required}
        size={props.size}
        styles={props.styles}
        type={props.type}
        value={props.value ?? undefined}
        maxWidth={props.maxWidth}
      />
      <ErrorMessage
        errorMessage={showError ? errorMessage : undefined}
        reserveSpace={props.reserveErrorSpace}
      />
    </>
  )

  return props.label ? (
    <Label
      description={props.description}
      fullWidth={props.fullWidth}
      icon={props.labelIcon}
      required={props.required}
      text={props.label}
      color={props.labelColor}
      placement={props.placement}
    >
      {inputAndErrorMessage}
    </Label>
  ) : (
    inputAndErrorMessage
  )
}
